import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import PaginatedResponse from '@/types/paginated-response'
import useSubscribe from '@/components/notifications/useSubscribe'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import BulkEditValueDialog from './BulkEditValueDialog'
import ClearFieldValuesDialog from './ClearFieldValuesDialog'
import RemoveManualValuesDialog from './RemoveManualValuesDialog'

type BulkActionType = 'edit' | 'clear' | 'removeManual'

type BulkAction = {
  type: BulkActionType
  field: ProjectGridField
  selectedRows: Required<DocumentRow>[]
}

type PendingBulkEdit = {
  fieldId: string
  rowIds: Set<string>
  docIds: Set<string>
}

type BulkEditValueContextValue = {
  openBulkEdit: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  openClearConfirm: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  openRemoveManualConfirm: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  setPendingEdits: (
    fieldId: string,
    rowIdsByDoc: Map<string, Set<string>>,
  ) => void
  isCellPending: (rowId: string, fieldId: string) => boolean
  getRowsFromCache: (queryKey: QueryKey) => Required<DocumentRow>[]
  fetchAllMatchingRows: (field: ProjectGridField) => Promise<{
    allRows: Required<DocumentRow>[]
    allMatchingRows: Required<DocumentRow>[]
  }>
  pendingFieldId: string | null
  listQueryKey: QueryKey | undefined
  fetchAllPages: (() => Promise<void>) | undefined
  selectedAllDocsCount: number | undefined
}

const BulkEditValueContext = createContext<BulkEditValueContextValue>(
  {} as BulkEditValueContextValue,
)

export const useBulkEditValueContext = () => useContext(BulkEditValueContext)

type BulkEditValueProviderProps = {
  children: ReactNode
  listQueryKey: QueryKey
  fetchAllPages: (() => Promise<void>) | undefined
  selectedAllDocsCount: number | undefined
}

export default function BulkEditValueProvider({
  children,
  listQueryKey,
  fetchAllPages,
  selectedAllDocsCount,
}: BulkEditValueProviderProps) {
  const overlay = useOverlay()
  const queryClient = useQueryClient()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)

  const [pending, setPending] = useState<PendingBulkEdit | null>(null)
  const pendingFieldId = pending?.fieldId ?? null

  const completedDocIdsRef = useRef<Set<string>>(new Set())

  // Stops a duplicate report from starting a second refetch while the first
  // one is still in flight.
  const isFinishingRef = useRef(false)

  const finishPendingEdits = useCallback(async () => {
    if (isFinishingRef.current) return
    isFinishingRef.current = true
    try {
      await queryClient.refetchQueries({ queryKey: listQueryKey })
    } catch {
      // A failed refetch only means stale rows, so swallow it and still
      // clear the pending state. This runs on the last document, so nothing
      // else will clear it and the cells would show skeletons forever.
    } finally {
      isFinishingRef.current = false
      completedDocIdsRef.current = new Set()
      setPending(null)
    }
  }, [listQueryKey, queryClient])

  const markDocCompleted = useCallback(
    (docId: string) => {
      if (!pending?.docIds.has(docId)) return

      // The same doc gets reported done twice — once by the websocket and
      // once by the sync response — and a report from an earlier bulk edit
      // can land late, so this has to stay harmless to repeat.
      completedDocIdsRef.current.add(docId)

      const allDone = [...pending.docIds].every((id) =>
        completedDocIdsRef.current.has(id),
      )
      if (allDone) finishPendingEdits()
    },
    [pending, finishPendingEdits],
  )

  const setPendingEdits = useCallback(
    (fieldId: string, rowIdsByDoc: Map<string, Set<string>>) => {
      const rowIds = new Set<string>()
      rowIdsByDoc.forEach((ids) => ids.forEach((id) => rowIds.add(id)))

      completedDocIdsRef.current = new Set()
      setPending({ fieldId, rowIds, docIds: new Set(rowIdsByDoc.keys()) })
    },
    [],
  )

  const isCellPending = useCallback(
    (rowId: string, fieldId: string) =>
      pending?.fieldId === fieldId && pending.rowIds.has(rowId),
    [pending],
  )

  const getRowsFromCache = useCallback(
    (queryKey: QueryKey) => {
      const cached =
        queryClient.getQueryData<InfiniteData<PaginatedResponse<DocumentRow>>>(
          queryKey,
        )
      return (cached?.pages.flatMap((p) => p.results) ??
        []) as Required<DocumentRow>[]
    },
    [queryClient],
  )

  const fetchAllMatchingRows = useCallback(
    async (field: ProjectGridField) => {
      if (fetchAllPages) await fetchAllPages()
      const allRows = listQueryKey ? getRowsFromCache(listQueryKey) : []
      const allMatchingRows = allRows.filter(
        (row) => row.project_grid_id === field.project_grid_id,
      )
      return { allRows, allMatchingRows }
    },
    [fetchAllPages, getRowsFromCache, listQueryKey],
  )

  // Subscribe to doc_changes notifications for pending document IDs
  const subscribeBody = useMemo(() => {
    if (!pending?.docIds.size) return undefined
    return [
      {
        endpoint_path: '/v2/pd/documents',
        obj_ids: [...pending.docIds],
      },
    ]
  }, [pending])

  useSubscribe({
    onMessage: useCallback(
      (event: MessageEvent) => {
        const data = JSON.parse(event.data)
        if (data.action !== '/v2/pd-rules/doc_changes|new') return
        const docId: string | undefined =
          data.obj_id ?? data.stripped_partial_msg?.obj_id
        if (!docId) return

        markDocCompleted(docId)
      },
      [markDocCompleted],
    ),
    body: subscribeBody,
  })

  const openBulkEdit = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'edit', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const openClearConfirm = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'clear', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const openRemoveManualConfirm = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'removeManual', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const contextValue = useMemo(
    () => ({
      openBulkEdit,
      openClearConfirm,
      openRemoveManualConfirm,
      setPendingEdits,
      isCellPending,
      getRowsFromCache,
      fetchAllMatchingRows,
      pendingFieldId,
      listQueryKey,
      fetchAllPages,
      selectedAllDocsCount,
    }),
    [
      openBulkEdit,
      openClearConfirm,
      openRemoveManualConfirm,
      setPendingEdits,
      isCellPending,
      getRowsFromCache,
      fetchAllMatchingRows,
      pendingFieldId,
      listQueryKey,
      fetchAllPages,
      selectedAllDocsCount,
    ],
  )

  const commonProps = {
    overlay,
    field: bulkAction?.field as ProjectGridField,
    selectedRows: bulkAction?.selectedRows as Required<DocumentRow>[],
    workflowId: selectedWorkflow.id,
    workflowStateId: selectedWorkflowState?.id || '',
    listQueryKey,
    setPendingEdits,
  }

  return (
    <BulkEditValueContext.Provider value={contextValue}>
      {children}
      {bulkAction?.type === 'edit' && <BulkEditValueDialog {...commonProps} />}
      {bulkAction?.type === 'clear' && (
        <ClearFieldValuesDialog {...commonProps} />
      )}
      {bulkAction?.type === 'removeManual' && (
        <RemoveManualValuesDialog {...commonProps} />
      )}
    </BulkEditValueContext.Provider>
  )
}
